import Tool, { BaseTool, type ToolButtonComponent } from './Tool';
import React from 'react';
import { ApplicationView, useController } from '@aha-app/mvc';
import align from '../utils/align';
import ToolbarMenu from '../toolbars/ToolbarMenu';
import ToolbarHDivider from '../toolbars/ToolbarHDivider';
import ToolbarRow from '../toolbars/ToolbarRow';
import ToolButton from '../toolbars/ToolButton';
import Tippy, { useSingleton } from '@tippyjs/react';
import { TOOLTIP_DELAY_ALIGNMENT_MENU } from '../constants';
import { tippyPopoverPlugin } from '../utils/tippyPopoverPlugin';
import WhiteboardController from '../controllers/WhiteboardController';
import { type SingletonObject } from '../types';

const alignButton = (
  tool: BaseTool,
  icon: string,
  tooltipContent: React.ReactNode,
  tooltipPlacement: string | undefined,
  tippySingletonTarget: SingletonObject
) => {
  return ApplicationView(() => {
    const controller = useController(WhiteboardController);

    return (
      <Tippy content={tooltipContent} singleton={tippySingletonTarget}>
        <span>
          <ToolButton onClick={() => controller.actionApplyTool(tool)}>
            <aha-icon icon={icon} />
          </ToolButton>
        </span>
      </Tippy>
    );
  });
};

class ToolAlignLeft extends BaseTool {
  static toolName = 'Align left';

  static action({ controller, selection }) {
    align.alignItems(controller, selection, 'left');
  }

  static buttonComponent(tippySingletonTarget: SingletonObject) {
    return alignButton(
      ToolAlignLeft,
      'fa-regular fa-objects-align-left',
      'Align left',
      'left',
      tippySingletonTarget
    );
  }
}

class ToolAlignCenter extends BaseTool {
  static toolName = 'Align center';

  static action({ controller, selection }) {
    align.alignItems(controller, selection, 'center');
  }

  static buttonComponent(tippySingletonTarget: SingletonObject) {
    return alignButton(
      ToolAlignCenter,
      'fa-regular fa-objects-align-center-horizontal',
      'Align horizontal centers',
      'top',
      tippySingletonTarget
    );
  }
}

class ToolAlignRight extends BaseTool {
  static toolName = 'Align right';

  static action({ controller, selection }) {
    align.alignItems(controller, selection, 'right');
  }

  static buttonComponent(tippySingletonTarget: SingletonObject) {
    return alignButton(
      ToolAlignRight,
      'fa-regular fa-objects-align-right',
      'Align right',
      'top',
      tippySingletonTarget
    );
  }
}

class ToolAlignTop extends BaseTool {
  static toolName = 'Align top';

  static action({ controller, selection }) {
    align.alignItems(controller, selection, 'top');
  }

  static buttonComponent(tippySingletonTarget: SingletonObject) {
    return alignButton(
      ToolAlignTop,
      'fa-regular fa-objects-align-top',
      'Align top',
      'left',
      tippySingletonTarget
    );
  }
}

class ToolAlignMiddle extends BaseTool {
  static toolName = 'Align middle';

  static action({ controller, selection }) {
    align.alignItems(controller, selection, 'middle');
  }

  static buttonComponent(tippySingletonTarget: SingletonObject) {
    return alignButton(
      ToolAlignMiddle,
      'fa-regular fa-objects-align-center-vertical',
      'Align vertical centers',
      'bottom',
      tippySingletonTarget
    );
  }
}

class ToolAlignBottom extends BaseTool {
  static toolName = 'Align bottom';

  static action({ controller, selection }) {
    align.alignItems(controller, selection, 'bottom');
  }

  static buttonComponent(tippySingletonTarget: SingletonObject) {
    return alignButton(
      ToolAlignBottom,
      'fa-regular fa-objects-align-bottom',
      'Align bottom',
      'bottom',
      tippySingletonTarget
    );
  }
}

class ToolDistributeHorizontal extends BaseTool {
  static toolName = 'Distribute horizontally';

  static action({ controller, selection }) {
    align.distributeItems(controller, selection, 'horizontal');
  }

  static buttonComponent(tippySingletonTarget: SingletonObject) {
    return alignButton(
      ToolDistributeHorizontal,
      'fa-regular fa-distribute-spacing-horizontal',
      'Distribute horizontal spacing',
      'right',
      tippySingletonTarget
    );
  }
}

class ToolDistributeVertical extends BaseTool {
  static toolName = 'Distribute vertically';

  static action({ controller, selection }) {
    align.distributeItems(controller, selection, 'vertical');
  }

  static buttonComponent(tippySingletonTarget: SingletonObject) {
    return alignButton(
      ToolDistributeVertical,
      'fa-regular fa-distribute-spacing-vertical',
      'Distribute vertical spacing',
      'right',
      tippySingletonTarget
    );
  }
}

const Popup: React.FC = ApplicationView(() => {
  const [tippySingletonSource, tippySingletonTarget] = useSingleton();

  const AlignLeft = ToolAlignLeft.buttonComponent(tippySingletonTarget);
  const AlignCenter = ToolAlignCenter.buttonComponent(tippySingletonTarget);
  const AlignRight = ToolAlignRight.buttonComponent(tippySingletonTarget);
  const AlignTop = ToolAlignTop.buttonComponent(tippySingletonTarget);
  const AlignMiddle = ToolAlignMiddle.buttonComponent(tippySingletonTarget);
  const AlignBottom = ToolAlignBottom.buttonComponent(tippySingletonTarget);

  const DistributeHorz =
    ToolDistributeHorizontal.buttonComponent(tippySingletonTarget);
  const DistributeVert =
    ToolDistributeVertical.buttonComponent(tippySingletonTarget);

  return (
    <ToolbarMenu>
      <Tippy
        singleton={tippySingletonSource}
        delay={TOOLTIP_DELAY_ALIGNMENT_MENU}
        zIndex={12000}
        plugins={[tippyPopoverPlugin]}
      />

      <ToolbarRow>
        <AlignLeft />
        <AlignCenter />
        <AlignRight />
        <ToolbarHDivider />
        <DistributeHorz />
      </ToolbarRow>

      <ToolbarRow>
        <AlignTop />
        <AlignMiddle />
        <AlignBottom />
        <ToolbarHDivider />
        <DistributeVert />
      </ToolbarRow>
    </ToolbarMenu>
  );
});

const AlignDistributeButton: ToolButtonComponent = ApplicationView(() => {
  return (
    <ToolButton popup={<Popup />}>
      <aha-icon icon='fa-regular fa-objects-align-left' />
    </ToolButton>
  );
});

class ToolAlignDistribute extends Tool {
  static toolName = 'Alignment';

  static buttonComponent() {
    return AlignDistributeButton;
  }
}

export {
  ToolAlignDistribute,
  ToolAlignLeft,
  ToolAlignCenter,
  ToolAlignRight,
  ToolAlignTop,
  ToolAlignMiddle,
  ToolAlignBottom,
  ToolDistributeHorizontal,
  ToolDistributeVertical,
};
